import { BusinessVerificationStatus } from 'gql/generated-types/custom-graphql'

/** Tab param on the Account (Settings) page. */
export const BUSINESS_VERIFICATION_TAB = 'business-verification'

export const BUSINESS_VERIFICATION_PATH = `/dashboard/account/${BUSINESS_VERIFICATION_TAB}`

/** The practice still owes a submission: never asked before, or the last one was rejected. */
export const isBusinessVerificationActionNeeded = (
	status?: BusinessVerificationStatus | null
): boolean =>
	status === BusinessVerificationStatus.NotCollected || status === BusinessVerificationStatus.Failed

/** The latest submission is awaiting review or approved, so the form is read-only. */
export const isBusinessVerificationLocked = (status?: BusinessVerificationStatus | null): boolean =>
	status === BusinessVerificationStatus.Submitted || status === BusinessVerificationStatus.Verified

/** Financing enabled and payments not: the only practices business verification applies to. */
export const isLendingOnlyPractice = (
	isFinancingEnabled?: boolean,
	isPaymentEnabled?: boolean
): boolean => !!isFinancingEnabled && !isPaymentEnabled

type BusinessVerificationGate = {
	promptedAt?: string | null
	promptOverride?: boolean | null
	isLendingOnly: boolean
}

/**
 * Shown for every status once the backend has prompted the practice (`promptedAt`, set on its
 * first loan after launch), so it can follow its submission through review. A status alone
 * never shows it. A forced-on banner opens it too, so "Verify now" has somewhere to land.
 */
export const shouldShowBusinessVerificationTab = ({
	promptedAt,
	promptOverride,
	isLendingOnly
}: BusinessVerificationGate): boolean => isLendingOnly && (!!promptedAt || promptOverride === true)

/**
 * Lending-only is the outer gate. Within that, `settings.businessVerificationPromptOverride`
 * forces the banner on (`true`) or off (`false`); otherwise it shows once the practice is
 * prompted and still owes a submission.
 */
export const shouldShowBusinessVerificationBanner = ({
	status,
	promptedAt,
	promptOverride,
	isLendingOnly
}: BusinessVerificationGate & { status?: BusinessVerificationStatus | null }): boolean => {
	if (!isLendingOnly) {
		return false
	}
	return typeof promptOverride === 'boolean'
		? promptOverride
		: !!promptedAt && isBusinessVerificationActionNeeded(status)
}
